/**
 * TieredAreaPicker — service-area picker (Phase 1.8 → Directory detail
 * rebuild §4: search leads).
 *
 * Directory detail rebuild: the three stacked tier dropdowns are replaced
 * by ONE typeahead. Typing filters states + counties + ZIPs/cities across
 * the SAME lookups data the picker already fetches (client-side; no new
 * endpoint) and the grouped results call the EXISTING add paths. Selected
 * areas render as mist "place" chips (a category/wayfinding hue — NO state
 * meaning) with a pin glyph. The old progressive-disclosure tier browser
 * stays reachable behind a quiet "Browse all areas" disclosure so no flow
 * is orphaned; the search is the lead affordance.
 *
 * Preserved verbatim from Phase 1.8.x: every data query, the county
 * roll-up projection (all child ZIPs selected → a county chip), the
 * toggle-deselect semantics, the duplicate-row defenses, and the read-only
 * (canEdit=false) short-circuit — chips only, no search input.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, X } from 'lucide-react';
import { lookupsApi } from '@/lib/lookups';
import { formatCountyDisplay } from '@/lib/serviceAreas';
import { SearchField } from '@/components/primitives/SearchField';
import type {
  ProviderServiceArea,
  ProviderServiceAreaTier,
  ServiceAreaLookupRow,
  ServiceAreaZipForSearch,
} from '@/lib/types';

export interface TieredAreaPickerProps {
  tenantSlug: string;
  providerId: string;
  selected: ProviderServiceArea[];
  onMutated: () => Promise<void>;
  /** Phase 1.11 — read-only render when false. Defaults to true. */
  canEdit?: boolean;
}

const TIER_STALE_MS = 10 * 60 * 1000;

export function TieredAreaPicker({
  tenantSlug,
  providerId,
  selected,
  onMutated,
  canEdit = true,
}: TieredAreaPickerProps): ReactNode {
  const tiersQuery = useQuery({
    queryKey: ['service-area-tiers', tenantSlug],
    queryFn: ({ signal }) => lookupsApi.listServiceAreaTiers(tenantSlug, signal),
    staleTime: TIER_STALE_MS,
    enabled: canEdit,
  });
  const statesQuery = useQuery({
    queryKey: ['lookups', 'service-areas', 'state', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listServiceAreas(tenantSlug, { area_type: 'state', limit: 500 }, signal),
    staleTime: TIER_STALE_MS,
    enabled: canEdit,
  });
  const countiesQuery = useQuery({
    queryKey: ['lookups', 'service-areas', 'county', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listServiceAreas(tenantSlug, { area_type: 'county', limit: 500 }, signal),
    staleTime: TIER_STALE_MS,
    enabled: canEdit,
  });

  const [busy, setBusy] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);

  /** county_id → child-ZIPs lookup from the tier data. */
  const zipsByCountyId = useMemo(() => {
    const m = new Map<string, ServiceAreaZipForSearch[]>();
    for (const z of tiersQuery.data?.zips ?? []) {
      let arr = m.get(z.county_id);
      if (!arr) {
        arr = [];
        m.set(z.county_id, arr);
      }
      arr.push(z);
    }
    return m;
  }, [tiersQuery.data?.zips]);

  /** zip_code → {city, county_display} for the selected-ZIP chip labels. */
  const zipInfoByCode = useMemo(() => {
    const m = new Map<string, { city: string | null; county_display: string }>();
    for (const z of tiersQuery.data?.zips ?? []) {
      m.set(z.zip_code, { city: z.city, county_display: z.county_display });
    }
    return m;
  }, [tiersQuery.data?.zips]);

  /** Selected ZIP set + state slug set + derived county coverage. */
  const derived = useMemo(() => {
    const selectedZipCodes = new Set<string>();
    const selectedStateSlugs = new Set<string>();
    const persistedCountySlugs = new Set<string>();
    for (const s of selected) {
      if (s.tier === 'zip') selectedZipCodes.add(s.slug);
      else if (s.tier === 'state') selectedStateSlugs.add(s.slug);
      else if (s.tier === 'county') persistedCountySlugs.add(s.slug);
    }

    const countyRows = countiesQuery.data?.rows ?? [];
    const countyCoverage = new Map<
      string,
      {
        row: ServiceAreaLookupRow;
        totalZips: number;
        selectedZips: number;
        childZipCodes: string[];
        fullySelected: boolean;
      }
    >();
    for (const cnty of countyRows) {
      const childZips = zipsByCountyId.get(cnty.id) ?? [];
      const totalZips = childZips.length;
      let selectedZips = 0;
      const childZipCodes: string[] = [];
      for (const z of childZips) {
        childZipCodes.push(z.zip_code);
        if (selectedZipCodes.has(z.zip_code)) selectedZips++;
      }
      countyCoverage.set(cnty.id, {
        row: cnty,
        totalZips,
        selectedZips,
        childZipCodes,
        fullySelected: totalZips > 0 && selectedZips === totalZips,
      });
    }

    const fullyCoveredCountyIds = new Set<string>();
    for (const [cid, cov] of countyCoverage) {
      if (cov.fullySelected) fullyCoveredCountyIds.add(cid);
    }

    const zipCodeToCountyId = new Map<string, string>();
    for (const z of tiersQuery.data?.zips ?? []) {
      zipCodeToCountyId.set(z.zip_code, z.county_id);
    }
    const partialZipCodes: string[] = [];
    for (const zc of selectedZipCodes) {
      const cid = zipCodeToCountyId.get(zc);
      if (!cid || !fullyCoveredCountyIds.has(cid)) partialZipCodes.push(zc);
    }
    partialZipCodes.sort();

    return {
      selectedZipCodes,
      selectedStateSlugs,
      persistedCountySlugs,
      countyCoverage,
      fullyCoveredCountyIds,
      partialZipCodes,
    };
  }, [selected, countiesQuery.data?.rows, zipsByCountyId, tiersQuery.data?.zips]);

  const refresh = useCallback(async () => {
    await onMutated();
  }, [onMutated]);

  // --- add / remove primitives (unchanged behavior) ------------------

  async function addCountyAsZips(countyId: string) {
    if (busy) return;
    const cov = derived.countyCoverage.get(countyId);
    if (!cov) return;
    const childZips = zipsByCountyId.get(countyId) ?? [];
    setBusy(true);
    try {
      for (const z of childZips) {
        if (derived.selectedZipCodes.has(z.zip_code)) continue;
        await lookupsApi.addProviderServiceArea(tenantSlug, providerId, {
          tier: 'zip',
          zip_id: z.id,
        });
      }
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function removeCounty(countyId: string) {
    if (busy) return;
    const childZips = zipsByCountyId.get(countyId) ?? [];
    const childZipCodeSet = new Set(childZips.map((z) => z.zip_code));
    const rowsToRemove = selected.filter(
      (s) => s.tier === 'zip' && childZipCodeSet.has(s.slug),
    );
    setBusy(true);
    try {
      for (const r of rowsToRemove) {
        await lookupsApi.removeProviderServiceArea(tenantSlug, providerId, r.id);
      }
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function removeOneRowByTierAndSlug(tier: ProviderServiceAreaTier, slug: string) {
    const row = selected.find((s) => s.tier === tier && s.slug === slug);
    if (!row) return;
    await lookupsApi.removeProviderServiceArea(tenantSlug, providerId, row.id);
    await refresh();
  }

  async function removeAllRowsByTierAndSlug(tier: ProviderServiceAreaTier, slug: string) {
    const rows = selected.filter((s) => s.tier === tier && s.slug === slug);
    if (rows.length === 0) return;
    setBusy(true);
    try {
      for (const r of rows) {
        await lookupsApi.removeProviderServiceArea(tenantSlug, providerId, r.id);
      }
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  // --- toggle handlers shared by the search + the browse-all browser --

  const stateIsSelected = useCallback(
    (r: ServiceAreaLookupRow) => derived.selectedStateSlugs.has(r.slug),
    [derived.selectedStateSlugs],
  );
  const countyIsSelected = useCallback(
    (r: ServiceAreaLookupRow) => {
      const cov = derived.countyCoverage.get(r.id);
      if (cov?.fullySelected) return true;
      return selected.some((s) => s.tier === 'county' && s.slug === r.slug);
    },
    [derived.countyCoverage, selected],
  );
  const zipIsSelected = useCallback(
    (r: ServiceAreaZipForSearch) => derived.selectedZipCodes.has(r.zip_code),
    [derived.selectedZipCodes],
  );

  async function toggleState(r: ServiceAreaLookupRow) {
    if (derived.selectedStateSlugs.has(r.slug)) {
      await removeAllRowsByTierAndSlug('state', r.slug);
    } else {
      await lookupsApi.addProviderServiceArea(tenantSlug, providerId, { slug: r.slug });
      await refresh();
    }
  }

  async function toggleCounty(r: ServiceAreaLookupRow) {
    const cov = derived.countyCoverage.get(r.id);
    const persistedRows = selected.filter((s) => s.tier === 'county' && s.slug === r.slug);
    const isSelected = Boolean(cov?.fullySelected) || persistedRows.length > 0;
    if (isSelected) {
      if (persistedRows.length > 0) await removeAllRowsByTierAndSlug('county', r.slug);
      if (cov?.fullySelected) await removeCounty(r.id);
    } else {
      await addCountyAsZips(r.id);
    }
  }

  async function toggleZip(r: ServiceAreaZipForSearch) {
    if (derived.selectedZipCodes.has(r.zip_code)) {
      await removeOneRowByTierAndSlug('zip', r.zip_code);
    } else {
      await lookupsApi.addProviderServiceArea(tenantSlug, providerId, {
        tier: 'zip',
        zip_id: r.id,
      });
      await refresh();
    }
  }

  // --- read-only short-circuit (chips only) --------------------------
  if (!canEdit) {
    if (selected.length === 0) return null;
    return (
      <SelectedPlaces
        selected={selected}
        derived={derived}
        zipInfoByCode={zipInfoByCode}
        busy={false}
        readOnly
      />
    );
  }

  return (
    <div className="space-y-3">
      <SelectedPlaces
        selected={selected}
        derived={derived}
        zipInfoByCode={zipInfoByCode}
        busy={busy}
        onRemoveState={async (slug) => removeAllRowsByTierAndSlug('state', slug)}
        onRemoveCountyPersisted={async (slug) => removeAllRowsByTierAndSlug('county', slug)}
        onRemoveCountyDerived={async (countyId) => removeCounty(countyId)}
        onRemoveZip={async (zipCode) => removeOneRowByTierAndSlug('zip', zipCode)}
        onRemoveLegacyById={async (id) => {
          await lookupsApi.removeProviderServiceArea(tenantSlug, providerId, id);
          await refresh();
        }}
      />

      <AreaSearch
        states={statesQuery.data?.rows ?? []}
        counties={countiesQuery.data?.rows ?? []}
        zips={tiersQuery.data?.zips ?? []}
        loading={statesQuery.isLoading || countiesQuery.isLoading || tiersQuery.isLoading}
        busy={busy}
        stateIsSelected={stateIsSelected}
        countyIsSelected={countyIsSelected}
        zipIsSelected={zipIsSelected}
        onPickState={toggleState}
        onPickCounty={toggleCounty}
        onPickZip={toggleZip}
      />

      <p className="text-xs text-ink-soft">
        Type a place (Morristown, 07039, or Bergen County) and pick from the list.
      </p>

      {/* Browse all areas — the old tier browser, kept reachable so no flow
          is orphaned; search leads. */}
      <div>
        <button
          type="button"
          onClick={() => setBrowseOpen((v) => !v)}
          aria-expanded={browseOpen}
          className="text-xs font-medium text-sage-600 underline decoration-sage-600/30 underline-offset-2 hover:decoration-sage-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight"
        >
          {browseOpen ? 'Hide the full area list' : 'Browse all areas'}
        </button>
        {browseOpen && (
          <div className="mt-2 space-y-2">
            <TierSection
              label="State"
              searchLabel="Search states"
              rows={statesQuery.data?.rows ?? []}
              loading={statesQuery.isLoading}
              formatLabel={(r) => r.display_name}
              isSelectedRow={stateIsSelected}
              onClick={toggleState}
              disabled={busy}
            />
            <TierSection
              label="County"
              searchLabel="Search counties"
              rows={countiesQuery.data?.rows ?? []}
              loading={countiesQuery.isLoading}
              formatLabel={(r) => formatCountyDisplay(r.display_name)}
              isSelectedRow={countyIsSelected}
              onClick={toggleCounty}
              disabled={busy}
            />
            <TierSection
              label="ZIP"
              searchLabel="Search ZIP codes"
              rows={tiersQuery.data?.zips ?? []}
              loading={tiersQuery.isLoading}
              formatLabel={(r: ServiceAreaZipForSearch) =>
                r.city ? `${r.zip_code} · ${r.city}` : r.zip_code
              }
              formatSubtitle={(r: ServiceAreaZipForSearch) => r.county_display}
              searchKey={(r: ServiceAreaZipForSearch) =>
                `${r.zip_code} ${r.city ?? ''} ${r.county_display}`
              }
              isSelectedRow={zipIsSelected}
              onClick={toggleZip}
              disabled={busy}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// Selected "place" chips — mist wayfinding hue + pin glyph
// =====================================================================

interface SelectedPlacesDerived {
  selectedZipCodes: Set<string>;
  selectedStateSlugs: Set<string>;
  persistedCountySlugs: Set<string>;
  countyCoverage: Map<
    string,
    {
      row: ServiceAreaLookupRow;
      totalZips: number;
      selectedZips: number;
      childZipCodes: string[];
      fullySelected: boolean;
    }
  >;
  fullyCoveredCountyIds: Set<string>;
  partialZipCodes: string[];
}

function SelectedPlaces({
  selected,
  derived,
  zipInfoByCode,
  busy,
  readOnly = false,
  onRemoveState,
  onRemoveCountyDerived,
  onRemoveCountyPersisted,
  onRemoveZip,
  onRemoveLegacyById,
}: {
  selected: ProviderServiceArea[];
  derived: SelectedPlacesDerived;
  zipInfoByCode: Map<string, { city: string | null; county_display: string }>;
  busy: boolean;
  readOnly?: boolean;
  onRemoveState?: (slug: string) => Promise<void>;
  onRemoveCountyDerived?: (countyId: string) => Promise<void>;
  onRemoveCountyPersisted?: (slug: string) => Promise<void>;
  onRemoveZip?: (zipCode: string) => Promise<void>;
  onRemoveLegacyById?: (id: string) => Promise<void>;
}): ReactNode {
  const stateChipsBySlug = new Map<string, string>();
  for (const s of selected) {
    if (s.tier === 'state' && !stateChipsBySlug.has(s.slug)) {
      stateChipsBySlug.set(s.slug, s.display_name);
    }
  }
  const stateChips = [...stateChipsBySlug.entries()]
    .map(([slug, label]) => ({ slug, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const countyChipBySlug = new Map<
    string,
    { slug: string; label: string; countyId: string | null }
  >();
  for (const s of selected) {
    if (s.tier !== 'county') continue;
    if (!countyChipBySlug.has(s.slug)) {
      countyChipBySlug.set(s.slug, {
        slug: s.slug,
        label: formatCountyDisplay(s.display_name),
        countyId: null,
      });
    }
  }
  for (const cid of derived.fullyCoveredCountyIds) {
    const cov = derived.countyCoverage.get(cid);
    if (!cov) continue;
    const existing = countyChipBySlug.get(cov.row.slug);
    if (existing) existing.countyId = cid;
    else
      countyChipBySlug.set(cov.row.slug, {
        slug: cov.row.slug,
        label: formatCountyDisplay(cov.row.display_name),
        countyId: cid,
      });
  }
  const countyChips = [...countyChipBySlug.values()].sort((a, b) =>
    a.label.localeCompare(b.label),
  );

  const legacyChips = selected
    .filter((s) => s.tier === 'msa' || s.tier === 'metro_division' || s.tier === 'csa')
    .map((s) => ({ id: s.id, label: s.short_name ?? s.display_name }));

  const zipChips = derived.partialZipCodes.map((zc) => {
    const info = zipInfoByCode.get(zc);
    return { zc, label: info?.city ? `${zc} · ${info.city}` : zc };
  });

  const total =
    stateChips.length + countyChips.length + legacyChips.length + zipChips.length;

  if (total === 0) {
    return (
      <div className="flex min-h-[2rem] items-center">
        <span className="text-xs italic text-ink-soft">
          Search below to add a service area.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {stateChips.map((c) => (
        <PlaceChip
          key={`state-${c.slug}`}
          label={c.label}
          busy={busy}
          onRemove={readOnly ? undefined : () => onRemoveState?.(c.slug)}
        />
      ))}
      {countyChips.map((c) => (
        <PlaceChip
          key={`county-${c.slug}`}
          label={c.label}
          busy={busy}
          onRemove={
            readOnly
              ? undefined
              : async () => {
                  await onRemoveCountyPersisted?.(c.slug);
                  if (c.countyId) await onRemoveCountyDerived?.(c.countyId);
                }
          }
        />
      ))}
      {legacyChips.map((c) => (
        <PlaceChip
          key={`legacy-${c.id}`}
          label={c.label}
          busy={busy}
          onRemove={readOnly ? undefined : () => onRemoveLegacyById?.(c.id)}
        />
      ))}
      {zipChips.map((c) => (
        <PlaceChip
          key={`zip-${c.zc}`}
          label={c.label}
          busy={busy}
          onRemove={readOnly ? undefined : () => onRemoveZip?.(c.zc)}
        />
      ))}
    </div>
  );
}

/** A selected place: mist chip (category/wayfinding hue) with a pin glyph. */
function PlaceChip({
  label,
  busy,
  onRemove,
}: {
  label: string;
  busy?: boolean;
  onRemove?: () => void | Promise<void>;
}): ReactNode {
  return (
    <span className="inline-flex items-center gap-1 rounded-tight bg-mist px-2 py-0.5 text-[11px] font-medium text-mist-deep">
      <MapPin size={11} aria-hidden="true" className="shrink-0" />
      {label}
      {onRemove && (
        <button
          type="button"
          disabled={busy}
          onClick={() => void onRemove()}
          aria-label={`Remove ${label}`}
          className="ml-0.5 rounded-tight opacity-70 hover:opacity-100 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          <X size={12} aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

// =====================================================================
// The typeahead — filters states + counties + ZIPs across the tier data
// =====================================================================

function AreaSearch({
  states,
  counties,
  zips,
  loading,
  busy,
  stateIsSelected,
  countyIsSelected,
  zipIsSelected,
  onPickState,
  onPickCounty,
  onPickZip,
}: {
  states: ServiceAreaLookupRow[];
  counties: ServiceAreaLookupRow[];
  zips: ServiceAreaZipForSearch[];
  loading: boolean;
  busy: boolean;
  stateIsSelected: (r: ServiceAreaLookupRow) => boolean;
  countyIsSelected: (r: ServiceAreaLookupRow) => boolean;
  zipIsSelected: (r: ServiceAreaZipForSearch) => boolean;
  onPickState: (r: ServiceAreaLookupRow) => Promise<void>;
  onPickCounty: (r: ServiceAreaLookupRow) => Promise<void>;
  onPickZip: (r: ServiceAreaZipForSearch) => Promise<void>;
}): ReactNode {
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();

  const matches = useMemo(() => {
    if (!needle) return null;
    const stateM = states
      .filter((r) => r.display_name.toLowerCase().includes(needle))
      .slice(0, 6);
    const countyM = counties
      .filter((r) => formatCountyDisplay(r.display_name).toLowerCase().includes(needle))
      .slice(0, 6);
    const zipM = zips
      .filter((r) =>
        `${r.zip_code} ${r.city ?? ''} ${r.county_display}`.toLowerCase().includes(needle),
      )
      .slice(0, 8);
    return { stateM, countyM, zipM, total: stateM.length + countyM.length + zipM.length };
  }, [needle, states, counties, zips]);

  return (
    <div>
      <SearchField
        value={search}
        onChange={setSearch}
        disabled={busy}
        aria-label="Add a service area"
        placeholder="State, county, or ZIP"
        testId="service-area-search"
      />

      {matches && (
        <div className="mt-2 overflow-hidden rounded-base border border-sage-200 bg-paper shadow-sh1">
          {loading ? (
            <div className="px-3 py-2 text-xs text-ink-soft">Loading…</div>
          ) : matches.total === 0 ? (
            <div className="px-3 py-2 text-xs italic text-ink-soft">
              No places match “{search.trim()}”.
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto py-1">
              <ResultGroup label="States">
                {matches.stateM.map((r) => (
                  <ResultRow
                    key={`s-${r.slug}`}
                    primary={r.display_name}
                    selected={stateIsSelected(r)}
                    disabled={busy}
                    onClick={() => void onPickState(r)}
                  />
                ))}
              </ResultGroup>
              <ResultGroup label="Counties">
                {matches.countyM.map((r) => (
                  <ResultRow
                    key={`c-${r.slug}`}
                    primary={formatCountyDisplay(r.display_name)}
                    selected={countyIsSelected(r)}
                    disabled={busy}
                    onClick={() => void onPickCounty(r)}
                  />
                ))}
              </ResultGroup>
              <ResultGroup label="ZIPs & towns">
                {matches.zipM.map((r) => (
                  <ResultRow
                    key={`z-${r.id}`}
                    primary={r.city ? `${r.zip_code} · ${r.city}` : r.zip_code}
                    subtitle={r.county_display}
                    selected={zipIsSelected(r)}
                    disabled={busy}
                    onClick={() => void onPickZip(r)}
                  />
                ))}
              </ResultGroup>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultGroup({ label, children }: { label: string; children: ReactNode }): ReactNode {
  const items = Array.isArray(children) ? children.filter(Boolean) : children;
  if (Array.isArray(items) && items.length === 0) return null;
  return (
    <div>
      <div className="px-3 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-sage-400">
        {label}
      </div>
      {children}
    </div>
  );
}

function ResultRow({
  primary,
  subtitle,
  selected,
  disabled,
  onClick,
}: {
  primary: string;
  subtitle?: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={
        'flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors disabled:opacity-50 ' +
        (selected ? 'bg-sage-50' : 'hover:bg-sand')
      }
    >
      <MapPin size={13} aria-hidden="true" className="shrink-0 text-sage-400" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-ink">{primary}</span>
        {subtitle && <span className="block truncate text-xs text-ink-soft">{subtitle}</span>}
      </span>
      {selected && <span className="shrink-0 text-xs font-semibold text-sage-600">Added ✓</span>}
    </button>
  );
}

// =====================================================================
// Browse-all tier section (unchanged progressive-disclosure browser)
// =====================================================================

interface TierSectionProps<TRow> {
  label: string;
  /** The tier's search box name and placeholder, such as "Search counties". */
  searchLabel: string;
  rows: ReadonlyArray<TRow>;
  loading: boolean;
  formatLabel: (r: TRow) => string;
  formatSubtitle?: (r: TRow) => string;
  searchKey?: (r: TRow) => string;
  isSelectedRow: (r: TRow) => boolean;
  onClick: (r: TRow) => Promise<void>;
  disabled?: boolean;
}

function TierSection<TRow extends { slug?: string; id?: string }>({
  label,
  searchLabel,
  rows,
  loading,
  formatLabel,
  formatSubtitle,
  searchKey,
  isSelectedRow,
  onClick,
  disabled,
}: TierSectionProps<TRow>): ReactNode {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const n = search.trim().toLowerCase();
    return rows.filter((r) => {
      const haystack = searchKey ? searchKey(r) : formatLabel(r);
      return haystack.toLowerCase().includes(n);
    });
  }, [rows, search, formatLabel, searchKey]);

  return (
    <div className="rounded-base border border-sage-800/[8%]">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between px-3 py-2 text-sm font-semibold text-ink hover:bg-sage-50"
      >
        <span>{label}</span>
        <span className="text-ink-soft">{isOpen ? '▾' : '▸'}</span>
      </button>
      {isOpen && (
        <div className="px-3 pb-3">
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder={searchLabel}
            aria-label={searchLabel}
            testId={`service-area-tier-search-${label.toLowerCase()}`}
            className="mb-2"
          />
          {loading ? (
            <div className="py-2 text-xs text-ink-soft">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-2 text-xs italic text-ink-soft">No matches.</div>
          ) : (
            <ul className="grid max-h-72 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2 md:grid-cols-3">
              {filtered.slice(0, 200).map((r) => {
                const key =
                  (r as { slug?: string; id?: string }).slug ??
                  (r as { id?: string }).id ??
                  '';
                const isSelected = isSelectedRow(r);
                const rowCls = isSelected
                  ? 'bg-sage-50 text-sage-800 border-sage-300 hover:bg-sage-100'
                  : 'hover:bg-sage-50 border-transparent hover:border-sage-200 text-ink';
                return (
                  <li key={key}>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => void onClick(r)}
                      className={`min-h-11 w-full rounded-base border px-2 py-1.5 text-left disabled:opacity-50 ${rowCls}`}
                    >
                      <div className="truncate text-sm">
                        {formatLabel(r)}
                        {isSelected && <span className="ml-1 text-xs text-sage-600">✓</span>}
                      </div>
                      {formatSubtitle && (
                        <div className="truncate text-xs text-ink-soft">{formatSubtitle(r)}</div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {filtered.length > 200 && (
            <p className="mt-1 text-xs italic text-ink-soft">
              {filtered.length - 200} more. Refine search to narrow.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
